import { uid } from '@w/utils'
import type { Block, BlockTR, BlockTD, ReviewTableRow, ReviewTableCell, BlockType, TextSegment, Chars, PageRef } from './block-types'
export type * from './block-types'

export function resolvePageRef(link: { pageId: string; spaceId?: string }, containingSpaceId: string): PageRef {
  return { spaceId: link.spaceId ?? containingSpaceId, pageId: link.pageId }
}

// Inline format bitmask — matches Lexical's TextNode format bits so Φ is 1:1.
// b/i/del/u/code wrappers are gone; format lives on chars block instead.
// UNDERLINE=8 is used by our serializer but has no standard md syntax; emitted as ::u[...].
// Decision: "Inline format = bitmask on chars block, not wrapper blocks" (decisions.md).
export enum FormatFlag {
  BOLD = 1,
  ITALIC = 2,
  STRIKE = 4,
  UNDERLINE = 8,
  CODE = 16,
}

export function unwrapReviewTableRows(
  children: ReadonlyArray<BlockTR | ReviewTableRow>,
): BlockTR[] {
  return children.flatMap((child) =>
    child.type === 'review' ? child.children : [child],
  )
}

export function unwrapReviewTableCells(
  children: ReadonlyArray<BlockTD | ReviewTableCell>,
): BlockTD[] {
  return children.flatMap((child) =>
    child.type === 'review' ? child.children : [child],
  )
}

export const RULE_SCOPES = [
  'inflection',
  'syntax',
  'derivation',
  'contrast',
  'collocation',
] as const
export function isBlockLevelBlock(type: BlockType) {
  // oxlint-disable-next-line typescript/switch-exhaustiveness-check -- intentional partial match; default returns false
  switch (type) {
    case 'h':
    case 'quote':
    case 'alert':
    case 'rule':
    case 'questionGroup':
    case 'p':
    case 'pageTitle':
    case 'list':
    case 'table':
    case 'pre':
    case 'hr':
    case 'img':
    case 'video':
    case 'audio':
    case 'file':
    case 'page':
    case 'pageLink':
    case 'pages':
    case 'question':
      return true
    default:
      return false
  }
}

export function diffMarker(kind: Extract<Block, { type: 'diff' }>['kind']) {
  if (kind === 'add') return '+'
  if (kind === 'delete') return '-'
  return ' '
}

export function isDiffCodeBlock(block: Extract<Block, { type: 'pre' }>): boolean {
  return (
    block.lang?.toLowerCase() === 'diff' &&
    block.children.every((child) => child.type === 'diff')
  )
}

/** IDs in imported block templates are replaced by the document on insertion. */
export const blockId = () => uid(4)

export const textToBlockLetters = (text: string): TextSegment[] => [{ id: '', text }]

export const makeChars = (d: {
  id?: string
  text: string
  format?: number
}): Block => ({
  type: 'chars',
  id: d.id ?? '',
  text: textToBlockLetters(d.text),
  ...(d.format ? { format: d.format } : {}),
})

// Read visible text without interpreting segment identities.
export const getTextFromChars = (block: Chars) =>
  block.text.map(segment => segment.text).join('')
